import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import {
  CaretDownIcon,
  CaretRightIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import {
  DIVISION_PERCENT_NOT_APPLICABLE,
  type GetPricelistResponse,
} from '@erp/rpc';
import {
  displayCompanyCode,
  formatVendorNumbers,
  pricelistVendorName,
} from '@/components/company/companyPickerDisplay';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
// A real <button>: Radix's `asChild` does not make a bare SVG focusable, so a
// raw icon trigger is unreachable by keyboard.
import {
  DateField,
  Field,
  ReceivedAsField,
  SectionLabel,
  receivedAsLabel,
} from './pricelistDetailFields';
// Parses at local midnight, so a stored date never reads as the previous day
// for anyone behind UTC — the whole reason these are DATEONLY columns.
import { formatDateOnlyString } from '@/utils/dateUtils';
import { formatFilesReadyDate } from './pricelistCaseFilesReady';
import { CompanyTags } from './CompanyTags';
import { PricelistDivisionTable } from './PricelistDivisionTable';
import { usePricelistDetailsForm } from './usePricelistDetailsForm';
import type { PricelistDetailsForm } from './usePricelistDetailsForm';
import { divisionEntries } from './pricelistDetailsFormState';
import type { DivisionEntry } from './pricelistDetailsFormState';

/** The pricelist's upload metadata, never editable here. */
function UploadFields({ pricelist }: { pricelist: GetPricelistResponse }) {
  const gt = useGT();
  const csvDoc = pricelist.csvDocuments[0];
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3">
      <Field label={<T context="pricelist details field label">Company</T>}>
        <span className="flex flex-wrap items-center gap-2">
          {pricelist.companyName !== null
            ? pricelistVendorName(pricelist.companyName)
            : gt('Unassigned', {
                context:
                  'pricelist details company field when none is assigned',
              })}
          {/* Beside the company, because that's whose tag it is. */}
          <CompanyTags shipAndDebitEnabled={pricelist.shipAndDebitEnabled} />
        </span>
      </Field>
      <Field
        label={<T context="pricelist details field label">Company Code</T>}
      >
        {displayCompanyCode(pricelist.originId) ?? '—'}
      </Field>
      <Field
        label={<T context="pricelist details field label">Vendor Number</T>}
      >
        {formatVendorNumbers(pricelist.vendorNumbers) || '—'}
      </Field>
      <Field label={<T context="pricelist details field label">Uploaded by</T>}>
        {pricelist.uploadedByName ?? '—'}
      </Field>
      <Field label={<T context="pricelist details field label">Uploaded at</T>}>
        {new Date(pricelist.createdAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </Field>
      <Field
        label={<T context="pricelist details field label">Extracted CSV</T>}
      >
        {csvDoc?.name ??
          gt('None', {
            context: 'pricelist details CSV field when no extraction exists',
          })}
      </Field>
    </div>
  );
}

/**
 * One division value as a chip: "%RD O 1.5%", "%RM N/A" when marked as not
 * applying, or "%RM —" when not reviewed yet.
 */
function ValueChip({
  label,
  percent,
}: {
  label: string;
  /** The value as saved, or null for a division with nothing recorded. */
  percent: string | null;
}) {
  if (percent === null) {
    return (
      <span className="rounded-md border border-dashed px-2 py-1 text-sm text-muted-foreground">
        {label} —
      </span>
    );
  }
  return (
    <span className="rounded-md border px-2 py-1 text-sm font-medium text-foreground">
      {label}{' '}
      {percent === DIVISION_PERCENT_NOT_APPLICABLE ? percent : `${percent}%`}
    </span>
  );
}

/** One folded division of the no-change pane. */
interface NoChangeItem {
  key: string;
  label: string;
}

/**
 * The divisions with no value recorded at all — not reviewed yet — folded
 * behind a chevron. An explicit 0% or N/A is a recorded decision, so it shows
 * as a chip with the rest and only the truly blank divisions fold here, each
 * reading as a dashed "%RM —" (GEN-2576).
 */
function NoChangeDivisions({ items }: { items: NoChangeItem[] }) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const divisionCount = items.length;
  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-fit gap-1 px-2 text-xs text-muted-foreground"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        {open ? (
          <CaretDownIcon className="h-3 w-3" />
        ) : (
          <CaretRightIcon className="h-3 w-3" />
        )}
        {divisionCount === 1
          ? gt('1 division not reviewed yet', {
              context:
                'toggle for the collapsed list of divisions with no price change recorded yet',
            })
          : gt('{count} divisions not reviewed yet', {
              context:
                'toggle for the collapsed list of divisions with no price change recorded yet',
              count: String(divisionCount),
            })}
      </Button>
      {open && (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <ValueChip key={item.key} label={item.label} percent={null} />
          ))}
        </div>
      )}
    </div>
  );
}

/** The saved values of the editable fields, as read-only text. */
function SavedDetails({ form }: { form: PricelistDetailsForm }) {
  const gt = useGT();
  // Divisions split by whether they carry a value: every recorded entry — 0%
  // included, since an explicit 0% is a recorded value — shows as a chip, and
  // only the divisions with nothing recorded fold as "no change".
  const recordedEntries: DivisionEntry[] = [];
  const noChangeItems: NoChangeItem[] = [];
  for (const division of form.divisions) {
    const filled = divisionEntries(division, form.values).filter(
      (entry) =>
        (form.values.percentByEntryKey[entry.key]?.trim() ?? '') !== '',
    );
    if (filled.length === 0) {
      noChangeItems.push({
        key: division.id,
        label: division.title.toUpperCase(),
      });
    } else {
      recordedEntries.push(...filled);
    }
  }
  const anticipated = form.values.anticipatedPercent.trim();
  const notes = form.values.notes.trim();

  return (
    <>
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <Field
          label={
            <T context="label for the anticipated percentage from the vendor price change notification">
              ANTICIPATED % Per Vendor Price Change Notification
            </T>
          }
        >
          {/* Free text since GEN-2577 — shown as typed, so a range or several
              per-group lines read as the vendor wrote them. */}
          <span className="whitespace-pre-wrap">
            {anticipated === '' ? '—' : anticipated}
          </span>
        </Field>
        <Field
          label={
            <T context="label for the tariff-driven price change field">
              Resulting from tariff change
            </T>
          }
        >
          {form.values.fromTariffChange
            ? gt('Yes', { context: 'boolean field value' })
            : gt('No', { context: 'boolean field value' })}
        </Field>
        <Field
          label={
            <T context="label for the date a pricelist's new prices take effect">
              Effective date
            </T>
          }
        >
          {formatDateOnlyString(form.values.effectiveDate, '—')}
        </Field>
        <Field
          label={
            <T context="label for the date a pricelist was loaded into the AS/400">
              AS/400 upload date
            </T>
          }
        >
          {formatDateOnlyString(form.values.erpUploadDate, '—')}
        </Field>
        <Field
          label={
            <T context="label for what the vendor sent for a price change">
              Received as
            </T>
          }
        >
          {form.values.receivedAs === ''
            ? '—'
            : receivedAsLabel(gt, form.values.receivedAs)}
        </Field>
        <Field
          label={
            <T context="label for the date a price change arrived from the vendor">
              Received date
            </T>
          }
        >
          {formatDateOnlyString(form.values.receivedDate, '—')}
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel>
          <T context="section heading for recorded price change per division">
            Recorded price change by division
          </T>
        </SectionLabel>
        {recordedEntries.length === 0 && noChangeItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <T context="empty state when no division price change is recorded">
              No division price changes recorded.
            </T>
          </p>
        ) : (
          <>
            {recordedEntries.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {recordedEntries.map((entry) => (
                  <ValueChip
                    key={entry.key}
                    label={entry.label}
                    percent={
                      form.values.percentByEntryKey[entry.key]?.trim() ?? ''
                    }
                  />
                ))}
              </div>
            )}
            {noChangeItems.length > 0 && (
              <NoChangeDivisions items={noChangeItems} />
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <SectionLabel>
          <T context="section heading for pricelist user notes">Notes</T>
        </SectionLabel>
        {notes === '' ? (
          <p className="text-sm text-muted-foreground">
            <T context="empty state when a pricelist has no notes">
              No notes yet.
            </T>
          </p>
        ) : (
          <p className="text-sm whitespace-pre-wrap text-foreground">{notes}</p>
        )}
      </div>
    </>
  );
}

/** The editable fields: vendor percentage, tariff flag, divisions, notes. */
function DetailsEditor({
  form,
  notesPlaceholder,
}: {
  form: PricelistDetailsForm;
  notesPlaceholder?: string;
}) {
  const gt = useGT();

  return (
    <>
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex w-80 flex-col gap-1">
          <Label
            htmlFor="pricelist-anticipated-percent"
            className="text-xs text-muted-foreground"
          >
            <T context="label for the anticipated percentage from the vendor price change notification">
              ANTICIPATED % Per Vendor Price Change Notification
            </T>
          </Label>
          {/* Free text, not a percent input (GEN-2577): vendors quote ranges
              and several per-group figures for one file, all of which land in
              one cell of the price file log. */}
          <Textarea
            id="pricelist-anticipated-percent"
            rows={2}
            value={form.values.anticipatedPercent}
            disabled={form.isSaving}
            placeholder={gt('e.g. 4 to 10%, or one line per product group', {
              context:
                'placeholder for the free-text anticipated change field on a pricelist',
            })}
            onChange={(event) => form.setAnticipatedPercent(event.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 pb-1.5">
          <Checkbox
            id="pricelist-from-tariff-change"
            checked={form.values.fromTariffChange}
            disabled={form.isSaving}
            onCheckedChange={(checked) =>
              form.setFromTariffChange(checked === true)
            }
          />
          <Label
            htmlFor="pricelist-from-tariff-change"
            className="text-sm font-normal"
          >
            <T context="label for the tariff-driven price change checkbox">
              Resulting from tariff change
            </T>
          </Label>
        </div>
        <DateField
          id="pricelist-effective-date"
          label={
            <T context="label for the date a pricelist's new prices take effect">
              Effective date
            </T>
          }
          value={form.values.effectiveDate}
          disabled={form.isSaving}
          onChange={form.setEffectiveDate}
        />
        <DateField
          id="pricelist-erp-upload-date"
          label={
            <T context="label for the date a pricelist was loaded into the AS/400">
              AS/400 upload date
            </T>
          }
          value={form.values.erpUploadDate}
          disabled={form.isSaving}
          onChange={form.setErpUploadDate}
        />
        <ReceivedAsField
          value={form.values.receivedAs}
          disabled={form.isSaving}
          onChange={form.setReceivedAs}
        />
        <DateField
          id="pricelist-received-date"
          label={
            <T context="label for the date a price change arrived from the vendor">
              Received date
            </T>
          }
          value={form.values.receivedDate}
          disabled={form.isSaving}
          onChange={form.setReceivedDate}
        />
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel>
          <T context="section heading for recorded price change per division">
            Recorded price change by division
          </T>
        </SectionLabel>
        {form.divisions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <T context="empty state when the org has no divisions (data sources)">
              No divisions available.
            </T>
          </p>
        ) : (
          <PricelistDivisionTable form={form} />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label
          htmlFor="pricelist-notes"
          className="text-xs font-medium tracking-wide text-muted-foreground uppercase"
        >
          <T context="section heading for pricelist user notes">Notes</T>
        </Label>
        <Textarea
          id="pricelist-notes"
          value={form.values.notes}
          rows={4}
          disabled={form.isSaving}
          placeholder={
            notesPlaceholder ??
            gt('Add notes about this pricelist…', {
              context: 'placeholder for the pricelist notes field',
            })
          }
          onChange={(event) => form.setNotes(event.target.value)}
        />
      </div>
    </>
  );
}

/** Pencil in view mode; Cancel/Save while editing. */
function CardActions({ form }: { form: PricelistDetailsForm }) {
  const gt = useGT();
  if (!form.isEditing) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={form.startEditing}
        aria-label={gt('Edit details', {
          context: 'aria label for the pricelist details edit (pencil) button',
        })}
      >
        <PencilSimpleIcon className="h-4 w-4" />
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {form.invalidKeys.length > 0 && (
        <span className="text-xs text-destructive">
          <T context="validation hint for invalid pricelist percentage fields">
            Enter percentages like 3.5 or -1.25, or N/A
          </T>
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={form.cancelEditing}
        disabled={form.isSaving}
      >
        <T context="cancel editing the pricelist details">Cancel</T>
      </Button>
      <Button
        size="sm"
        onClick={form.save}
        disabled={
          form.isSaving ||
          !form.isDirty ||
          form.invalidKeys.length > 0 ||
          // The payload keeps only the divisions on offer, so saving before
          // the list loads would wipe every saved division percentage.
          !form.divisionsReady
        }
      >
        <T context="save pricelist detail edits">Save details</T>
      </Button>
    </div>
  );
}

/**
 * The details card for whichever record holds the values — a file's legacy
 * details row or a pricelist update case.
 *
 * Parameterised rather than forked: the nine fields, the division table and
 * the validation are identical for both, so the only differences are the
 * read-only context fields above them (upload metadata for a file; the vendor
 * and case number for a case) and whether editing is allowed at all.
 */
export function PricelistDetailsCardBody({
  form,
  contextFields,
  readOnly = false,
  notice,
  notesPlaceholder,
}: {
  form: PricelistDetailsForm;
  /** Read-only fields rendered above the editable ones. */
  contextFields?: React.ReactNode;
  /** Hides the pencil. Used where another record owns the values. */
  readOnly?: boolean;
  /** A line explaining a read-only card, rendered beside the heading. */
  notice?: React.ReactNode;
  /** Names what the notes are about; the file page's wording by default. */
  notesPlaceholder?: string;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-md border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>
          <T context="heading for the pricelist details card">Details</T>
        </SectionLabel>
        {readOnly ? notice : <CardActions form={form} />}
      </div>
      {contextFields}
      {form.isEditing ? (
        <DetailsEditor form={form} notesPlaceholder={notesPlaceholder} />
      ) : (
        <SavedDetails form={form} />
      )}
    </div>
  );
}

/**
 * Everything known about one pricelist FILE in a single card: its upload
 * metadata, the anticipated vendor percentage, whether the change is
 * tariff-driven, the recorded change per division, and the notes. The pencil
 * switches the card between reading the saved values and editing them.
 *
 * When the file belongs to a pricelist update case the card turns read-only
 * and points at the case: the case then owns the domain (a document in
 * `im_pricelist_case_files` reads its metadata from its case), and the backend
 * refuses a legacy write for it. One writer, no dual-write.
 */
export function PricelistDetailsCard({
  pricelist,
}: {
  pricelist: GetPricelistResponse;
}) {
  const form = usePricelistDetailsForm(pricelist);
  const owningCase = pricelist.case;
  return (
    <PricelistDetailsCardBody
      form={form}
      readOnly={owningCase !== null}
      contextFields={<UploadFields pricelist={pricelist} />}
      notice={
        owningCase ? (
          <span className="flex flex-col items-end gap-0.5 text-xs">
            {owningCase.filesReadyAt !== null && (
              <span className="text-muted-foreground">
                <T context="pricelist file card, vendor files-ready date from the owning case">
                  Files ready{' '}
                  <Var>{formatFilesReadyDate(owningCase.filesReadyAt)}</Var>
                </T>
              </span>
            )}
            <Link
              to="/item-master/pricelist-cases/$caseId"
              params={{ caseId: owningCase.caseId }}
              className="font-medium text-primary hover:underline"
            >
              <T context="link from a pricelist file to the update case that owns its details">
                Recorded on price change{' '}
                <Var>{owningCase.caseNumber ?? ''}</Var> — set the upload
                reminder there
              </T>
            </Link>
          </span>
        ) : undefined
      }
    />
  );
}
